import { useContext } from 'react';

import { getReadablePayMonth } from '@fora-monorepo/employer-my-pages-common/utils';
import { PayMonthOverviewViewModel, PayMonthStatusEnum } from '@fora-monorepo/employer-my-pages-common/types';
import {
  Box,
  capitalizeString,
  Headings,
  Table,
  TableRowContext,
  Tag,
  TagVariant,
  theme,
  useMediaMin,
} from '@foradevelopment/fora-ui';

import { PayReportsTableRowExpanded } from '../../../components';
import { links } from '../../../router/links';
import { useAppSelector } from '../../../redux/hooks';
import { DittoKeys, useTypedTranslation } from '../../../i18n/i18n';

type PayReportsTableRowProps = {
  payMonth: PayMonthOverviewViewModel;
  isInitiallyExpanded?: boolean;
};

export const PayReportsTableRow = ({ payMonth, isInitiallyExpanded = false }: PayReportsTableRowProps) => {
  const { text, tagVariant, rowTitle, statusTestId } = getPayMonthStatusTagDetails(payMonth.payMonthStatus);
  const isAnyReportProcessing = useAppSelector(state => state.payroll.isAnyReportProcessing);
  const { t } = useTypedTranslation();

  return (
    <Table.Row isInitiallyExpanded={isInitiallyExpanded} testId={`test-id-${payMonth.payMonth}`}>
      <PayMonthCell payMonth={payMonth.payMonth} />
      <Table.Cell textAlign="right">
        <Tag text={t(text)} variant={tagVariant} hasWhiteOutline={true} testId={`${statusTestId}`} />
      </Table.Cell>
      <Box key="body" paddingBottom={60} paddingLeft={20} paddingRight={20} paddingTop={40}>
        <PayReportsTableRowExpanded
          payMonth={payMonth}
          rowTitle={t(rowTitle)}
          manualReportingLink={`${links.reportPayroll}/${payMonth.payMonth}`}
          isAnyReportProcessing={isAnyReportProcessing}
        />
      </Box>
    </Table.Row>
  );
};

const PayMonthCell = ({ payMonth }: { payMonth: string }) => {
  const { isRowExpanded } = useContext(TableRowContext);
  const isDesktopOrTablet = useMediaMin(theme.breakpoint.md);
  const { t } = useTypedTranslation();

  if (isRowExpanded && isDesktopOrTablet) {
    return (
      <Table.Cell>
        <Headings.H3>
          {t('section.reportDetails.expandedRowTitle', { payMonth: getReadablePayMonth(payMonth) })}
        </Headings.H3>
      </Table.Cell>
    );
  }
  return <Table.Cell>{capitalizeString(getReadablePayMonth(payMonth))}</Table.Cell>;
};

const getPayMonthStatusTagDetails = (
  status?: PayMonthStatusEnum
): {
  text: DittoKeys;
  tagVariant: TagVariant;
  rowTitle: DittoKeys;
  statusTestId: number;
} => {
  switch (status) {
    case PayMonthStatusEnum.ReportProcessing:
      return {
        text: 'status.reportProcessing.tagText',
        tagVariant: 'additionalGray',
        rowTitle: 'status.reportProcessing.tagText',
        statusTestId: 0,
      };
    case PayMonthStatusEnum.ReportWithDeviations:
      return {
        text: 'status.reportWithDeviations.tagText',
        tagVariant: 'error',
        rowTitle: 'status.reportWithDeviations.tagText',
        statusTestId: 1,
      };
    case PayMonthStatusEnum.ReportingNotDone:
      return {
        text: 'status.reportingNotDone.tagText',
        tagVariant: 'informational',
        rowTitle: 'status.reportingNotDone.rowTitle',
        statusTestId: 2,
      };
    case PayMonthStatusEnum.ReportingDelayed:
      return {
        text: 'status.reportingDelayed.tagText',
        tagVariant: 'error',
        rowTitle: 'status.reportingDelayed.rowTitle',
        statusTestId: 3,
      };
    case PayMonthStatusEnum.ReportingApproved:
      return {
        text: 'status.reportingApproved.tagText',
        tagVariant: 'success',
        rowTitle: 'status.reportingApproved.tagText',
        statusTestId: 4,
      };
    case PayMonthStatusEnum.NoActiveEmployments:
      return {
        text: 'status.noActiveEmployments.tagText',
        tagVariant: 'additionalGray',
        rowTitle: 'status.noActiveEmployments.tagText',
        statusTestId: 5,
      };
    default:
      return {
        text: 'status.reportingNotDone.tagText',
        tagVariant: 'informational',
        rowTitle: 'status.reportingNotDone.tagText',
        statusTestId: 2,
      };
  }
};
